/**
 * Site-wide search overlay.
 *
 * Uses the combobox pattern: the input keeps focus while arrow keys move an `aria-activedescendant`
 * cursor through the results, so keyboard and screen-reader users navigate the list without
 * losing the ability to keep typing.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from '@/components/ui';
import { useDebounced, useEscapeKey, useScrollLock } from '@/hooks';
import { cn } from '@/lib/cn';
import { siteApi } from '@/services/api';

const TYPE_LABELS: Record<string, string> = {
  product: 'Product',
  solution: 'Solution',
  service: 'Service',
  industry: 'Industry',
  case_study: 'Case study',
  article: 'Resource',
  page: 'Page',
  job: 'Career',
};

export function SearchDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [term, setTerm] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const debounced = useDebounced(term, 250);

  useScrollLock(open);
  useEscapeKey(onClose, open);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    } else {
      setTerm('');
      setCursor(0);
    }
  }, [open]);

  const { data, isFetching } = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => siteApi.search(debounced, 8),
    enabled: open && debounced.trim().length >= 2,
    staleTime: 60 * 1000,
  });

  const results = useMemo(() => data?.results ?? [], [data]);

  useEffect(() => setCursor(0), [debounced]);

  if (!open) return null;

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (results.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setCursor((current) => (current + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCursor((current) => (current - 1 + results.length) % results.length);
    } else if (event.key === 'Enter') {
      const target = results[cursor];
      if (target) {
        event.preventDefault();
        navigate(target.url);
        onClose();
      }
    }
  }

  const showEmpty = debounced.trim().length >= 2 && !isFetching && results.length === 0;

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center px-4 pt-[12vh]">
      <button type="button" aria-label="Close search" onClick={onClose} className="absolute inset-0 bg-ink/55 backdrop-blur-sm" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        className="relative z-10 w-full max-w-2xl animate-scale-in overflow-hidden rounded-xl border border-line bg-surface shadow-float"
      >
        <div className="flex items-center gap-3 border-b border-line px-5 py-4">
          <svg aria-hidden="true" viewBox="0 0 20 20" className="h-5 w-5 shrink-0 text-muted">
            <path d="M13 13l4 4M15 8.5a6.5 6.5 0 1 1-13 0 6.5 6.5 0 0 1 13 0Z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
          <input
            ref={inputRef}
            type="search"
            role="combobox"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search solutions, products, case studies, roles…"
            aria-label="Search the site"
            aria-expanded={results.length > 0}
            aria-controls="search-results"
            aria-autocomplete="list"
            aria-activedescendant={results[cursor] ? `search-option-${cursor}` : undefined}
            className="w-full border-0 bg-transparent p-0 text-base text-ink placeholder:text-muted/70 focus:outline-none"
          />
          {isFetching && <Spinner label="Searching" />}
          <kbd className="hidden shrink-0 rounded border border-line px-1.5 py-0.5 font-mono text-[0.625rem] text-muted sm:block">
            Esc
          </kbd>
        </div>

        <div className="max-h-[55vh] overflow-y-auto">
          {term.trim().length < 2 && (
            <p className="px-5 py-8 text-center text-sm text-muted">Type at least two characters to search.</p>
          )}

          {showEmpty && (
            <div className="px-5 py-8 text-center">
              <p className="text-sm font-medium text-ink">No matches for “{debounced}”.</p>
              <p className="mt-1 text-sm text-muted">Try a broader term, or contact us and describe the problem.</p>
            </div>
          )}

          {results.length > 0 && (
            <ul id="search-results" role="listbox" aria-label="Search results" className="divide-y divide-line">
              {results.map((result, index) => (
                <li key={`${result.type}-${result.url}`} role="none">
                  <button
                    type="button"
                    id={`search-option-${index}`}
                    role="option"
                    aria-selected={index === cursor}
                    onMouseEnter={() => setCursor(index)}
                    onClick={() => {
                      navigate(result.url);
                      onClose();
                    }}
                    className={cn(
                      'flex w-full flex-col items-start gap-1 px-5 py-3.5 text-left transition-colors',
                      index === cursor ? 'bg-primary-soft' : 'hover:bg-raised',
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <span className="rounded-full bg-sunken px-2 py-0.5 text-[0.625rem] font-bold uppercase tracking-wide text-muted">
                        {TYPE_LABELS[result.type] ?? result.type}
                      </span>
                      <span className="font-medium text-ink">{result.title}</span>
                    </span>
                    {result.summary && <span className="line-clamp-2 text-sm text-muted">{result.summary}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {results.length > 0 && (
          <p className="border-t border-line bg-raised px-5 py-2.5 text-xs text-muted">
            {data?.total} result{data?.total === 1 ? '' : 's'} · Use ↑ ↓ to navigate, Enter to open
          </p>
        )}
      </div>
    </div>
  );
}
